/* eslint-disable @typescript-eslint/restrict-template-expressions */
import dayjs from 'dayjs'
import React from 'react'
import ReactApexChart, { type Props } from 'react-apexcharts'
import { type BarGraphParameter } from '../../KpiDetails'
import { type KpiKey } from '../../redux/changeKpiType'
import { getToggleOptions } from './kpiUtils'
import LanguageStrings from '../../../../i18n/locales'

const barChartString = LanguageStrings().Charts.BarChart

export interface seriesData {
  name: string
  data: number[]
}

export interface ReportData {
  name: string
  type: string
  categories: string[]
  series: seriesData[]
  colors: string[]
  suffix: string
}

function darkenColor (hexColor: string, amount: number): string {
  if (!/^#([0-9A-F]{3}){1,2}$/i.test(hexColor)) {
    return hexColor
  }

  hexColor = hexColor.replace(/^#/, '')

  const r: number = parseInt(hexColor.slice(0, 2), 16)
  const g: number = parseInt(hexColor.slice(2, 4), 16)
  const b: number = parseInt(hexColor.slice(4, 6), 16)

  const newR: number = Math.max(0, Math.round(r - r * amount))
  const newG: number = Math.max(0, Math.round(g - g * amount))
  const newB: number = Math.max(0, Math.round(b - b * amount))

  const newHexColor = `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`

  return newHexColor
}

export function getReactApexChartBar (reportData: ReportData, height: string, isHistory: boolean, setBarGraphParameter?: React.Dispatch<React.SetStateAction<BarGraphParameter>>, selectedKpi?: KpiKey, barGraphParameter?: BarGraphParameter, selectedDonut?: number): any {
  const categories = reportData.categories
  const highestValue = Math.max(...reportData.series.map((g) => {
    return Math.max(...g.data)
  }))

  const chartOptions: Props = {
    series: reportData.series.map((g, idx) => {
      return {
        name: g.name,
        data: g.data.map((d, i) => {
          if ((d / highestValue * 100) < 2 && d !== 0) {
            return highestValue * 0.02
          }
          return d
        })
      }
    }),
    options: {
      states: {
        active: {
          filter: {
            type: 'none'
          }
        }
      },
      chart: {
        height: 300,
        type: 'bar',
        background: 'transparent',
        toolbar: {
          show: false
        },
        events: {
          dataPointSelection: function (event, chartContext, config) {
            if (isHistory && setBarGraphParameter !== undefined && selectedKpi !== undefined) {
              setBarGraphParameter((prev) => { return { date: dayjs(categories[config.dataPointIndex]), bar: getToggleOptions(selectedKpi)[config.seriesIndex] } })
            }
          }
        },
        zoom: {
          enabled: false
          // autoScaleYaxis: true
        }
      },
      theme: { mode: 'dark' },
      colors: [function (value: any) {
        console.log(value, selectedDonut, categories[value.dataPointIndex], barGraphParameter?.date.format('YYYY-MM-DD'), reportData.series[value.seriesIndex].name, reportData.name[value.dataPointIndex])
        if (selectedDonut !== undefined && value.dataPointIndex !== undefined && value.seriesIndex !== undefined && categories[value.dataPointIndex] === barGraphParameter?.date.format('YYYY-MM-DD')) {
          return darkenColor(reportData.colors[value.seriesIndex], 0.5)
        } else if (value.dataPointIndex !== undefined && value.seriesIndex !== undefined && categories[value.dataPointIndex] === barGraphParameter?.date.format('YYYY-MM-DD') && reportData.series[value.seriesIndex].name === barGraphParameter.bar.label) {
          return darkenColor(reportData.colors[value.seriesIndex], 0.5)
        }
        return reportData.colors[value.seriesIndex]
      }],
      plotOptions: {
        bar: {
          borderRadius: 2,
          columnWidth: '45%',
          dataLabels: {
            position: 'center' // top, center, bottom
          }
        }
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['#313031']
      },
      dataLabels: {
        enabled: false,
        offsetY: -20,
        style: {
          fontSize: '12px',
          colors: ['#fff']
        }
      },
      xaxis: {
        categories,
        tickPlacement: 'on',
        position: 'bottom',
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: true
        },
        crosshairs: {
          fill: {
            type: 'gradient',
            gradient: {
              colorFrom: '#D8E3F0',
              colorTo: '#BED1E6',
              stops: [0, 100],
              opacityFrom: 0.4,
              opacityTo: 0.5
            }
          }
        },
        tooltip: {
          enabled: false
        },
        labels: {
          show: true,
          rotate: (window.innerWidth < 1550 && isHistory) ? -45 : 0,
          rotateAlways: (window.innerWidth < 1550 && isHistory),
          style: {
            colors: '#979798'
          },
          formatter: function (val) {
            const date = new Date(val).toDateString().split(' ')
            if (isHistory) {
              return `${date[2]} ${date[1]}`
            } else {
              return `${date[0]}`
            }
          }
        }
      },
      yaxis: {
        axisBorder: {
          show: false
        },
        // decimalsInFloat: 0,
        axisTicks: {
          show: false
        },
        labels: {
          show: true,
          showDuplicates: false,
          style: {
            colors: '#bbbbbc'
          },
          formatter: function (val, opt) {
            const s: string = reportData.suffix
            if (opt?.seriesIndex !== undefined && opt?.dataPointIndex !== undefined && reportData.series[opt.seriesIndex] !== undefined) {
              const dVal = Math.round(reportData.series[opt.seriesIndex].data[opt.dataPointIndex])
              return `${dVal} ${s} ${dVal === 1 ? barChartString.unit : barChartString.units}`
            } else {
              return `${Math.round(val)} ${s} ${Math.round(val) === 1 ? barChartString.unit : barChartString.units}`
            }
          }
        }
      },
      legend: {
        show: true,
        onItemClick: {
          toggleDataSeries: false
        },
        onItemHover: {
          highlightDataSeries: false
        }
      }
    }
  }

  return (
    <ReactApexChart
      options={chartOptions.options}
      series={chartOptions.series}
      type='bar' height={height}
    />
  )
}
